/*
 * ZoevaAnalytics — the single analytics façade for the theme.
 * ---------------------------------------------------------------------------
 * The REST of the stack should never talk to a tracking provider directly. It calls the
 * provider-neutral methods below with NEUTRAL product objects; this file is the ONE place that
 * knows about the current provider (Elevar) — its event names, payload shape, the dl_user_data
 * ordering requirement, and the push target (window.ElevarDataLayer). Swapping providers later
 * means rewriting only this file, not every caller.
 *
 * Neutral product object (what callers pass):
 *   { handle, sku, productId, variantId, title, vendor, productType, variantTitle,
 *     priceCents, compareAtCents, image, position }
 *
 * event_id / dedup: we never set an event_id — Elevar's listener generates one per event and
 * reuses it across the web + server sends (that's how those get deduped downstream). That does NOT
 * dedupe OUR duplicates, so callers must fire once per intent (e.g. featured-products.js guards its
 * on-scroll view with window.__zoevaFpViewFired), and the queue below flushes in emit order.
 *
 * Public API (provider-neutral, semantic):
 *   ZoevaAnalytics.productImpressions({ products, list, currency })  // a list of products was seen
 *   ZoevaAnalytics.productView({ product, list, currency })          // a product detail was viewed
 *   ZoevaAnalytics.productSelect({ product, list, currency })        // a product was clicked in a list
 */
(function () {
  if (window.ZoevaAnalytics) return;

  // Elevar owns window.ElevarDataLayer (an array). Create it if missing, but never overwrite a
  // non-array it might hold — just don't push into one (mirrors snippets/lp-elevar-view-item.liquid).
  const dataLayer = () => {
    if (window.ElevarDataLayer == null) window.ElevarDataLayer = [];
    return window.ElevarDataLayer;
  };
  const dlOk = () => Array.isArray(window.ElevarDataLayer);
  const hasUserData = () => dlOk() && window.ElevarDataLayer.some((e) => e && e.event === 'dl_user_data');

  // Reuse the user_properties Elevar's app attaches to its base dl_user_data event (device/page/
  // marketing are added by Elevar itself; we must not invent them).
  const userProps = () => {
    if (!dlOk()) return { visitor_type: 'guest' };
    const dl = window.ElevarDataLayer;
    for (let i = dl.length - 1; i >= 0; i--) {
      if (dl[i] && dl[i].event === 'dl_user_data' && dl[i].user_properties) return dl[i].user_properties;
    }
    return { visitor_type: 'guest' };
  };

  // Elevar requires its dl_user_data event FIRST, so events emitted before it are queued and
  // flushed — in emit order — once it arrives (cap the wait ~4s; if Elevar never loads we flush
  // anyway, harmless).
  const queue = [];
  const sent = []; // events this façade has actually pushed (for debug())
  let flushed = false;
  let polling = false;

  function pushNow(event) {
    if (!dlOk()) return; // never throw if something clobbered the data layer
    event.user_properties = userProps();
    window.ElevarDataLayer.push(event);
    sent.push(event);
  }

  // Drain queued events in emit-order, exactly once. Idempotent (guarded by `flushed`), so it's
  // safe from either the poll tick or emit() — whichever reaches dl_user_data first.
  function flushQueue() {
    if (flushed) return;
    flushed = true;
    while (queue.length) pushNow(queue.shift());
  }

  function startPolling() {
    if (polling) return;
    polling = true;
    let tries = 0;
    (function tick() {
      if (flushed) {
        polling = false;
        return; // emit() already drained the queue
      }
      if (hasUserData() || tries > 40) {
        flushQueue();
        polling = false;
        return;
      }
      tries += 1;
      setTimeout(tick, 100);
    })();
  }

  // Returns the event object that was emitted (handy when calling from the console).
  function emit(event) {
    dataLayer();
    if (flushed) {
      pushNow(event);
    } else if (hasUserData()) {
      // dl_user_data has arrived but a queued batch may still be pending (poll tick not yet
      // fired) — drain it FIRST so events keep their emit order, then push this one.
      flushQueue();
      pushNow(event);
    } else {
      queue.push(event);
      startPolling();
    }
    return event;
  }

  // ---- The only provider-specific mapping: neutral product -> Elevar product object ----
  const toElevarProduct = (p, list) => ({
    id: String(p.sku || ''),
    product_id: String(p.productId || ''),
    variant_id: String(p.variantId || ''),
    name: p.title || '',
    brand: p.vendor || '',
    category: p.productType || '',
    variant: p.variantTitle || '',
    price: ((Number(p.priceCents) || 0) / 100).toFixed(2),
    compare_at_price: ((Number(p.compareAtCents) || 0) / 100).toFixed(2),
    image: p.image || '',
    position: String(p.position || ''),
    list: list || '',
  });

  window.ZoevaAnalytics = {
    productImpressions(opts) {
      opts = opts || {};
      const products = opts.products || [];
      if (!products.length) return null; // nothing seen → don't fire an empty list event
      const list = opts.list || '';
      return emit({
        event: 'dl_view_item_list',
        ecommerce: {
          currencyCode: opts.currency,
          impressions: products.map((p) => toElevarProduct(p, list)),
        },
      });
    },
    productView(opts) {
      opts = opts || {};
      if (!opts.product) return null;
      return emit({
        event: 'dl_view_item',
        ecommerce: {
          currencyCode: opts.currency,
          detail: { actionField: { list: opts.list || '', action: 'detail' }, products: [toElevarProduct(opts.product, opts.list)] },
        },
      });
    },
    productSelect(opts) {
      opts = opts || {};
      if (!opts.product) return null;
      return emit({
        event: 'dl_select_item',
        ecommerce: {
          currencyCode: opts.currency,
          click: { actionField: { list: opts.list || '', action: 'click' }, products: [toElevarProduct(opts.product, opts.list)] },
        },
      });
    },

    // Console introspection: `window.ZoevaAnalytics.debug()` → a snapshot of the façade's state.
    debug() {
      const productsOf = (e) =>
        (e.ecommerce &&
          (e.ecommerce.impressions ||
            (e.ecommerce.detail && e.ecommerce.detail.products) ||
            (e.ecommerce.click && e.ecommerce.click.products))) ||
        [];
      return {
        provider: 'elevar',
        ready: hasUserData(), // dl_user_data present → events push immediately
        flushed, // the initial queued batch has been sent
        queued: queue.length, // events waiting for dl_user_data
        sent: sent.length, // events this façade has pushed
        userProperties: userProps(),
        elevarDataLayerLength: (window.ElevarDataLayer || []).length,
        lastSent: sent.slice(-8).map((e) => ({
          event: e.event,
          items: productsOf(e).map((p) => p.name || p.variant_id),
        })),
      };
    },
  };
})();
